'use strict'

import Swatch from './Swatch'
import SwatchAPI from '../api/SwatchAPI'
import SwatchColor from './SwatchColor'
import SwatchPage from './SwatchPage'
import Utils from '../Utils'

const DEFAULT_BLEACH_VALUE = 40
const DEFAULT_TEXTURE_SCALE = 0.6
const DEFAULT_TEXTURE_ROTATION = 0
const DEFAULT_TEXTURE_PIXEL_RESOLUTION = 300
const DEFAULT_TEXTURE_SIZE_IN_MM = 1000
const KEY_DB = Object.freeze({
    BLEACH_VALUE: 'bleach_value',
    TEXTURE_SCALE: 'texture_scale',
    TEXTURE_ROTATION: 'texture_rotation',
    TEXTURE_PIXEL_RESOLUTION: 'texture_pixel_resolution',
    TEXTURE_SIZE_IN_MM: 'texture_size_in_mm',
    TYPE: 'type',
})
const KEY_XML = Object.freeze({
    BLEACH_VALUE: 'luminosity',
    TEXTURE_SCALE: 'textureScale',
    TEXTURE_ROTATION: 'textureRotation',
    TEXTURE_PIXEL_RESOLUTION: 'texturePixelResolution',
    TEXTURE_SIZE_IN_MM: 'textureSizeInMM',
    TYPE: 'type',
})
const BLEACH_KEYS = [KEY_DB.BLEACH_VALUE, KEY_XML.BLEACH_VALUE]
const TEXTURE_SCALE_KEYS = [KEY_DB.TEXTURE_SCALE, KEY_XML.TEXTURE_SCALE]
const TEXTURE_ROTATION_KEYS = [KEY_DB.TEXTURE_ROTATION, KEY_XML.TEXTURE_ROTATION]
const TEXTURE_PIXEL_RES_KEYS = [KEY_DB.TEXTURE_PIXEL_RESOLUTION, KEY_XML.TEXTURE_PIXEL_RESOLUTION]
const TEXTURE_SIZE_KEYS = [KEY_DB.TEXTURE_SIZE_IN_MM, KEY_XML.TEXTURE_SIZE_IN_MM]
const TYPE_KEYS = [KEY_DB.TYPE, KEY_XML.TYPE]
const TYPE = Object.freeze({
    COLOR: 0,
    TEXTURE: 1,
    TEXTURE_SWATCH: 2,
})

/**
 * Color
 * - Keeps your constructor parameter names.
 * - No async in constructor; call `await color.init()`.
 * - Class-wide logging helpers: toggle with `Color.DEBUG = true` when debugging.
 */
class Color {
    /** -------------------- Class-wide logging helpers -------------------- */
    /** Flip this in DevTools to enable/disable logs everywhere. */
    static DEBUG = false
    /** All logs share this label. Change to taste. */
    static LOG_LABEL = '[Color]'

    /** Console passthroughs gated by DEBUG (global to the class). */
    static log(...args) {
        if (Color.DEBUG) console.debug(Color.LOG_LABEL, ...args)
    }
    static warn(...args) {
        if (Color.DEBUG) console.warn(Color.LOG_LABEL, ...args)
    }

    /**
     * Construct a lightweight Color instance.
     *
     * Initializes core fields and relationships to safe defaults.
     * Does not capture heavy context (payload, project); those are provided to `init()`.
     *
     * @param {Object} [params]
     * @param {number} [params.dropcolor_position=1]  Position/index for this color.
     */
    constructor({ dropcolor_position = 1 } = {}) {
        // Basic state
        this.dropcolor_id = dropcolor_position
        this.type = TYPE.COLOR
        this.note = ''
        this.name = ''
        this.swatchColorCode = null
        this.swatchColorName = null
        this.swatchColorInfos = null

        // Initialize
        this.resetSwatchLinks()
        this.resetTexture()
    }

    /**
     * Reset all texture-related state.
     */
    resetTexture() {
        this.texture = undefined
        this.texture_page = undefined
        this.texture_swatch = undefined
        this.texture_full_url = undefined
        this.texture_data_base64 = undefined
        this.texture_canvas_name = undefined
    }

    /**
     * Reset all swatch-related state (non-texture).
     */
    resetSwatchLinks() {
        this.swatch_page = undefined
        this.swatch_color = undefined
        this.swatch = undefined
    }

    /**
     * Initialize the instance with contextual data.
     *
     * Selects an initialization path based on the shape of `payload`
     * and prepares swatch-related models and optional texture data.
     *
     * @param {Object}  [ctx]
     * @param {Object}  [ctx.payload]   Swatch-color data in any supported shape.
     * @param {Project} [ctx.project]   Optional project context for lookups.
     * @returns {Promise<Color>}        Resolves to this instance when ready.
     */
    async init({ payload: payload, current_swatch_id: current_swatch_id, listOfColorChartsServerID: listOfColorChartsServerID }) {
        Color.log('init')
        console.log(payload)

        if (isFilePayload(payload)) {
            await this._initFromFilePayload({
                filePayload: payload,
                current_swatch_id: current_swatch_id,
                listOfColorChartsServerID: listOfColorChartsServerID,
            })
        } else if (isProjectPayload(payload)) {
            await this._initFromProjectPayload({
                obj: payload,
                current_swatch_id: current_swatch_id,
                listOfColorChartsServerID: listOfColorChartsServerID,
            })
        } else if (isDbColorPayload(payload)) {
            await this._initFromDbColorPayload({
                obj: payload,
                current_swatch_id: current_swatch_id,
                listOfColorChartsServerID: listOfColorChartsServerID,
            })
        } else {
            await this._initFromSwatchLikePayload(payload, current_swatch_id)
        }

        Color.log('init complete', {
            color: this,
        })
        this.applyDefaultNameIfEmpty()
        return this
    }

    /**
     * Create and initialize a Color with the given context.
     *
     * Builds the instance with the provided position, then runs `init()` with
     * the supplied swatch-color data and optional project.
     *
     * @param {Object}  [args]
     * @param {Object}  [args.swatch_color_data]  Payload used during initialization.
     * @param {number}  [args.dropcolor_position=1]
     * @param {Project} [args.project]
     * @returns {Promise<Color>}                  Fully initialized instance.
     */
    static async create({
        swatch_color_data: swatch_color_data,
        dropcolor_position = 1,
        current_swatch_id: current_swatch_id,
        listOfColorChartsServerID: listOfColorChartsServerID,
    } = {}) {
        const c = new Color({ dropcolor_position })
        // Only used here, not stored on the instance
        if (swatch_color_data) c.setColorProperties(swatch_color_data) // optional: keep the quick sync parse
        return c.init({
            payload: swatch_color_data,
            current_swatch_id: current_swatch_id,
            listOfColorChartsServerID: listOfColorChartsServerID,
        })
    }

    /** -------------------- Public constants & helpers -------------------- */
    static get DEFAULT_TEXTURE_SCALE() {
        return DEFAULT_TEXTURE_SCALE
    }
    static get DEFAULT_TEXTURE_ROTATION() {
        return DEFAULT_TEXTURE_ROTATION
    }
    static get DEFAULT_BLEACH_VALUE() {
        return DEFAULT_BLEACH_VALUE
    }
    static get DEFAULT_TEXTURE_PIXEL_RESOLUTION() {
        return DEFAULT_TEXTURE_PIXEL_RESOLUTION
    }
    static get DEFAULT_TEXTURE_SIZE_IN_MM() {
        return DEFAULT_TEXTURE_SIZE_IN_MM
    }
    static get TYPE() {
        return TYPE
    }

    static defaultSurfaceLabel() {
        if (typeof window !== 'undefined') {
            return String(window?.VisualizerConfig?.labels?.surface || '').trim()
        }
        return ''
    }

    /** -------------------- Private constants & helpers -------------------- */

    colorHasTextureOrTextureSwatch() {
        return this.type === TYPE.TEXTURE || this.type === TYPE.TEXTURE_SWATCH
    }

    /**
     * Ensure export-ready swatch fields are attached to this Color (and swatch_color when present).
     * Falls back to swatch color values and computed infos when missing.
     * @returns {{swatchColorCode: string, swatchColorName: string, swatchColorInfos: string}}
     */
    ensureExportSwatchData() {
        const swatch = this.swatch_color && typeof this.swatch_color === 'object' ? this.swatch_color : null
        const swatchColorCode = this.swatchColorCode || swatch?.swatchColorCode || swatch?.name || ''
        const swatchColorName = this.swatchColorName || swatch?.swatchColorName || swatch?.name2 || ''

        let swatchColorInfos = this.swatchColorInfos || swatch?.swatchColorInfos || ''
        if (!swatchColorInfos && swatch && swatch.sr != null && swatch.sg != null && swatch.sb != null) {
            swatchColorInfos = Utils.setSwatchColorInfos(swatch.sr, swatch.sg, swatch.sb)
        }

        this.swatchColorCode = swatchColorCode
        this.swatchColorName = swatchColorName
        this.swatchColorInfos = swatchColorInfos

        if (swatch) {
            swatch.swatchColorCode = swatchColorCode
            swatch.swatchColorName = swatchColorName
            swatch.swatchColorInfos = swatchColorInfos
        }

        return { swatchColorCode, swatchColorName, swatchColorInfos }
    }

    /**
     * Set base64 image + canvas name from an image source.
     * @param {HTMLImageElement|ImageBitmap|CanvasImageSource} img
     * @param {number} [position=this.dropcolor_id]
     */
    setTextureImage(img, position = this.dropcolor_id) {
        this.texture_canvas_name = `viewimage_texture_${position}`
        this.texture_data_base64 = Utils.getBase64Image(img)
        Color.log('setTextureImage', { canvas: this.texture_canvas_name })
    }

    /**
     * Compute and assign the texture image URL for the current instance.
     * @returns {void}
     */
    setTexturePath() {
        // TEXTURE: needs swatch.id and swatch_color.color_filename
        if (this.type === TYPE.TEXTURE) {
            if (!this.swatch?.id || !this.swatch_color?.color_filename) return
            this.texture_full_url = `/swatches/${this.swatch.id}/color/${this.swatch_color.color_filename}`
        }
        // TEXTURE_SWATCH: needs texture_swatch.id and texture.image_filename
        else if (this.type === TYPE.TEXTURE_SWATCH) {
            if (!this.texture_swatch?.id || !this.texture?.image_filename) return
            this.texture_full_url = `/texture-swatches/${this.texture_swatch.id}/image/${this.texture.image_filename}`
        }
        // COLOR (no texture): clear any previous URL
        else {
            this.texture_full_url = undefined
        }
        Color.log('setTexturePath', { type: this.type, url: this.texture_full_url })
    }

    /**
     * Set swatch models on this instance from a raw swatch-color payload.
     *
     * Input:
     * - `raw`: object describing a swatch color; may optionally include:
     *    - `swatch_pages`: array; the first entry (if any) is used to build `this.swatch_page`
     *    - `swatch`: object; used to build `this.swatch`
     *
     * @param {Object} raw
     * @returns {void}
     */
    setSwatchColor(raw) {
        // log the incoming payload shape for debugging
        Color.log('setSwatchColor input', raw)

        // always create the SwatchColor model from the raw payload
        this.swatch_color = new SwatchColor(raw)

        // if a swatch page array is present and non-empty, take the first page
        if ('swatch_pages' in raw && Array.isArray(raw.swatch_pages) && raw.swatch_pages.length > 0) {
            this.swatch_page = new SwatchPage(raw.swatch_pages[0])
        } else {
            // otherwise just note that no page info was provided
            Color.log('setSwatchColor: no swatch_page in payload')
        }

        // if an embedded swatch object exists, hydrate it
        if ('swatch' in raw) {
            this.swatch = new Swatch(raw.swatch)
        } else {
            // otherwise note its absence (the color itself is still valid)
            Color.log('setSwatchColor: no swatch in payload')
        }
    }

    /**
     * Set texture-swatch fields when in TEXTURE_SWATCH mode.
     */
    setTextureSwatch(tex) {
        if (this.type !== TYPE.TEXTURE_SWATCH || !tex) return
        this.texture = tex
        this.texture_page = tex.texture_page
        this.texture_swatch = tex.texture_swatch
        Color.log('setTextureSwatch', { textureId: this.texture_swatch?.id })
    }

    /**
     * Remove the applied texture/texture-swatch and keep the underlying swatch color.
     * Returns the instance to allow fluent repaint/update pipelines.
     * @returns {Color}
     */
    clearTextureAssignment() {
        this.type = TYPE.COLOR
        this.resetTexture()
        delete this.texture_scale
        delete this.texture_rotation
        delete this.texturePixelResolution
        delete this.textureSizeInMM
        return this
    }

    /**
     * Centralized normalization + import of numeric/meta props.
     * Accepts either a flat object or `{ attributes: {...} }`.
     *
     * Notes on number handling (mirrors your original):
     * - bleach_value / luminosity → **Math.floor(...)**
     * - texture_rotation / PixelResolution / SizeInMM → **integer** via Math.trunc(...)
     * - texture_scale → **float** (no rounding), with sane default
     */
    setColorProperties(input = {}) {
        // unwrap file payloads that carry data under `attributes`
        const raw = input && typeof input === 'object' && 'attributes' in input ? input.attributes || {} : input
        const o = raw && typeof raw === 'object' ? raw : {}

        // small helpers for consistent parsing
        const toNum = (v) => {
            const n = Number(v)
            return Number.isFinite(n) ? n : NaN
        }
        const withDefault = (n, d) => (Number.isFinite(n) ? n : d)
        const floorOr = (v, d) => {
            const n = toNum(v)
            return Number.isFinite(n) ? Math.floor(n) : d
        }
        const truncOr = (v, d) => {
            const n = toNum(v)
            return Number.isFinite(n) ? Math.trunc(n) : d
        }
        const pick = (...keys) => {
            for (const key of keys) {
                if (key in o) return o[key]
            }
            return undefined
        }

        // optional explicit type override (accept numeric or numeric string)
        const typeValue = pick(...TYPE_KEYS)
        if (typeValue != null) {
            const t = toNum(typeValue)
            if ([TYPE.COLOR, TYPE.TEXTURE, TYPE.TEXTURE_SWATCH].includes(t)) this.type = t
        }

        // ---- Numeric props ----
        // bleach_value (DB: bleach_value) → floor like your original
        this.bleach_value = floorOr(pick(...BLEACH_KEYS), DEFAULT_BLEACH_VALUE)

        // texture_scale: keep as float; default to constant
        this.texture_scale = withDefault(toNum(pick(...TEXTURE_SCALE_KEYS)), DEFAULT_TEXTURE_SCALE)

        // texture_rotation: integer degrees (trunc)
        this.texture_rotation = truncOr(pick(...TEXTURE_ROTATION_KEYS), DEFAULT_TEXTURE_ROTATION)

        // texturePixelResolution: integer if present
        const texPixel = pick(...TEXTURE_PIXEL_RES_KEYS)
        if (texPixel != null) this.texturePixelResolution = truncOr(texPixel, DEFAULT_TEXTURE_PIXEL_RESOLUTION)
        else this.texturePixelResolution = this.texturePixelResolution ?? DEFAULT_TEXTURE_PIXEL_RESOLUTION

        // textureSizeInMM: integer; default constant if missing
        const texSize = pick(...TEXTURE_SIZE_KEYS)
        this.textureSizeInMM =
            texSize != null ? truncOr(texSize, DEFAULT_TEXTURE_SIZE_IN_MM) : (this.textureSizeInMM ?? DEFAULT_TEXTURE_SIZE_IN_MM)

        // ---- Pass-throughs (only set if present) ----
        if ('texture_canvas_name' in o) this.texture_canvas_name = o.texture_canvas_name
        if ('texture_data_base64' in o) this.texture_data_base64 = o.texture_data_base64
        if ('texture_full_url' in o) this.texture_full_url = o.texture_full_url

        // Swatch metadata (often present in file attributes)
        if ('swatchColorInfos' in o) this.swatchColorInfos = o.swatchColorInfos
        if ('swatchColorCode' in o) this.swatchColorCode = o.swatchColorCode
        if ('swatchColorName' in o) this.swatchColorName = o.swatchColorName
        //if ('swatchName' in o) this.swatchName = o.swatchName
        if ('swatchServerID' in o) this.swatchServerID = o.swatchServerID

        if ('color_filename' in o) this.imported_color_filename = o.color_filename
        if ('colorFilename' in o) this.imported_color_filename = o.colorFilename

        Color.log('setColorProperties applied', {
            bleach_value: this.bleach_value,
            texture_scale: this.texture_scale,
            texture_rotation: this.texture_rotation,
            textureSizeInMM: this.textureSizeInMM,
        })
    }

    /**
     * Ensure a display name exists from the translated "surface" label.
     */
    applyDefaultNameIfEmpty(slot = this.dropcolor_id) {
        const trimmed = (this.name || '').trim()
        if (trimmed) return

        const prefix = Color.defaultSurfaceLabel()
        const number = slot || this.dropcolor_id || ''
        this.name = prefix ? `${prefix} ${number}`.trim() : String(number)
    }

    /** -------------------- Private async init branches -------------------- */

    async _initFromFilePayload({
        filePayload: filePayload,
        current_swatch_id: current_swatch_id,
        listOfColorChartsServerID: listOfColorChartsServerID,
    } = {}) {
        Color.log('init:file-payload')

        // Import attrs and detect intended type
        const attrs = filePayload.attributes || {}
        this.setColorProperties(attrs)

        const hasTextureObj = 'texture' in filePayload
        this.type = hasTextureObj ? Color.TYPE.TEXTURE_SWATCH : 'colorImage' in filePayload ? Color.TYPE.TEXTURE : Color.TYPE.COLOR

        // 1) Swatch (exact vs closest)
        await this.hydrateSwatch({
            serverID: this.swatchServerID,
            colorCode: this.swatchColorCode,
            colorInfos: this.swatchColorInfos,
            current_swatch_id: current_swatch_id,
            listOfColorChartsServerID: listOfColorChartsServerID,
        })

        // 2) Texture (fetch/validate may downgrade to COLOR)
        if (this.type === Color.TYPE.TEXTURE) {
            const expectedFilename = attrs.color_filename ?? attrs.colorFilename
            await this.hydrateTexture({ expectedFilename, current_swatch_id })
        } else if (this.type === Color.TYPE.TEXTURE_SWATCH) {
            const textureName = filePayload?.texture?.textureName || filePayload?.texture?.attributes?.textureName
            await this.hydrateTextureSwatch(textureName)
        }
    }

    /** Project payload (`swatchColorInfos` present). */
    async _initFromProjectPayload({
        obj: obj,
        current_swatch_id: current_swatch_id,
        listOfColorChartsServerID: listOfColorChartsServerID,
    } = {}) {
        Color.log('init:project-payload')
        console.log('init:project-payload')

        // 1) Swatch
        await this.hydrateSwatch({
            serverID: obj.swatchServerID,
            colorCode: obj.swatchColorCode,
            colorInfos: obj.swatchColorInfos,
            current_swatch_id: current_swatch_id,
            listOfColorChartsServerID: listOfColorChartsServerID,
        })
        // 2) Texture
        if (this.type === Color.TYPE.TEXTURE) {
            await this.hydrateTexture({ current_swatch_id })
        } else if (this.type === Color.TYPE.TEXTURE_SWATCH) {
            const textureName = obj?.texture?.textureName || obj?.texture?.attributes?.textureName
            await this.hydrateTextureSwatch(textureName)
        }
    }

    // Returns one of Color.TYPE.COLOR | .TEXTURE | .TEXTURE_SWATCH
    detectColorTypeFromDbObject(obj) {
        // texture swatch exists only if we actually have a texture object/value
        const hasTextureSwatch = !!obj?.texture

        // texture (from file) exists if swatch_color has a color image filename
        const hasTextureFile = !!obj?.swatch_color?.color_filename

        return hasTextureSwatch ? Color.TYPE.TEXTURE_SWATCH : hasTextureFile ? Color.TYPE.TEXTURE : Color.TYPE.COLOR
    }

    /** DB color payload: has `swatch_color`, texture may be null. */
    async _initFromDbColorPayload({
        obj: obj,
        current_swatch_id: current_swatch_id,
        listOfColorChartsServerID: listOfColorChartsServerID,
    } = {}) {
        Color.log('init:db-color-payload')

        // Swatch is already a DB model here
        this.setSwatchColor(obj.swatch_color)

        this.type = this.detectColorTypeFromDbObject(obj)

        // Texture
        if (this.type === Color.TYPE.TEXTURE) {
            await this.hydrateTexture({
                current_swatch_id: current_swatch_id,
            })
        } else if (this.type === Color.TYPE.TEXTURE_SWATCH) {
            const textureName = obj?.texture?.name
            await this.hydrateTextureSwatch(textureName)
        }
    }

    /** Generic “replace swatch/texture” payload. */
    async _initFromSwatchLikePayload(obj, current_swatch_id) {
        Color.log('init:swatch-like-payload')

        this.setSwatchColor(obj)

        if (this.type === Color.TYPE.TEXTURE) {
            await this.hydrateTexture({ current_swatch_id })
        } else if (this.type === Color.TYPE.TEXTURE_SWATCH) {
            const textureName = obj?.texture?.textureName || obj?.texture?.attributes?.textureName
            await this.hydrateTextureSwatch(textureName)
        }
    }

    /** Load texture image when there’s a URL. */
    async _loadTextureImageIfAny() {
        if (!this.texture_full_url) return
        try {
            const img = await Utils.loadImage(this.texture_full_url)
            if (img) this.setTextureImage(img, this.dropcolor_id)
        } catch (e) {
            Color.warn('loadTextureImageIfAny failed', e)
        }
    }

    static normalizeServerID(id) {
        const map = {
            100000: 1,
            100001: 2,
            100002: 3,
            100003: 4,
            100004: 5,
            100005: 6,
            100006: 7,
            100007: 8,
            100008: 9,
            100009: 10,
            100010: 11,
            100011: 12,
            100012: 13,
            100013: 14,
            100014: 15,
            100017: 18,
            100019: 20,
            100021: 22,
            200000: 23,
            200001: 24,
            200002: 25,
            200003: 26,
        }
        const n = Number(id)
        return n in map ? map[n] : n
    }

    /**
     * Fetch and apply the swatch color (exact vs closest).
     * Returns true if a swatch was applied.
     */
    async hydrateSwatch({ serverID, colorCode, colorInfos, current_swatch_id, listOfColorChartsServerID }) {
        const normalized = Color.normalizeServerID(serverID)
        const inCharts = !!listOfColorChartsServerID?.includes(Number(normalized))
        const swatchId = current_swatch_id
        const canClosest = !inCharts && swatchId != null && !!colorInfos

        try {
            const swatchColor = canClosest
                ? await SwatchAPI.getClosestColor(colorInfos, swatchId)
                : await SwatchAPI.getSwatchColor(normalized, colorCode)

            if (swatchColor) {
                this.setSwatchColor(swatchColor)
                return true
            }
        } catch (e) {
            Color.warn('hydrateSwatch: color fetch failed', e)
        }
        return false
    }

    /**
     * TYPE.TEXTURE validation/finalization:
     * - assumes hydrateSwatch() already ran and set this.swatch_color + this.swatch
     * - validates DB color_filename against expected (from import) if provided
     * - on failure: downgrade to COLOR and try closest color
     * - on success: set URL and load image
     */
    async hydrateTexture({ expectedFilename, current_swatch_id } = {}) {
        if (this.type !== Color.TYPE.TEXTURE) return false

        const dbFilename = this.swatch_color?.color_filename
        const hasSwatchId = !!this.swatch?.id
        const expected = expectedFilename ?? this.imported_color_filename

        // Must have DB filename + swatch id to proceed
        if (!dbFilename || !hasSwatchId) {
            return await this.downgradeToClosest({ current_swatch_id })
        }

        // If the import provided a filename, it must match DB's canonical filename
        if (expected && expected !== dbFilename) {
            Color.warn('hydrateTexture: filename mismatch', { expected, dbFilename })
            return await this.downgradeToClosest({ current_swatch_id })
        }

        // Success → finalize URL + image
        this.setTexturePath()
        await this._loadTextureImageIfAny()
        return true
    }

    /**
     * TYPE.TEXTURE_SWATCH path:
     * - requires a textureName
     * - fetches via SwatchAPI.getTextureSwatch(textureName)
     * - on failure or missing name: downgrade to COLOR and clear texture state
     * - on success: apply texture swatch, set URL, load image
     */
    async hydrateTextureSwatch(textureName) {
        if (this.type !== Color.TYPE.TEXTURE_SWATCH) return false

        if (!textureName) {
            this.type = Color.TYPE.COLOR
            this.resetTexture()
            return false
        }

        try {
            const textureSwatch = await SwatchAPI.getTextureSwatch(textureName)
            if (!textureSwatch) {
                Color.warn('downgrade texture swatch to color')
                this.type = Color.TYPE.COLOR
                this.resetTexture()
                return false
            }
            this.setTextureSwatch(textureSwatch)
        } catch {
            Color.warn('hydrateTextureSwatch: fetch failed')
            Color.warn('downgrade texture swatch to color')
            this.type = Color.TYPE.COLOR
            this.resetTexture()
            return false
        }

        this.setTexturePath()
        await this._loadTextureImageIfAny()
        return true
    }

    /**
     * Downgrade to TYPE.COLOR, clear texture state, and try closest color.
     * Safe to call from multiple hydration paths.
     */
    async downgradeToClosest({ current_swatch_id } = {}) {
        this.type = Color.TYPE.COLOR
        this.resetTexture()

        try {
            const swatchId = current_swatch_id
            const colorInfos = this.swatchColorInfos
            if (swatchId != null && colorInfos) {
                const swatchColor = await SwatchAPI.getClosestColor(colorInfos, swatchId)
                if (swatchColor) this.setSwatchColor(swatchColor)
            }
        } catch (e) {
            Color.warn('downgradeToClosest: closest-color fetch failed', e)
        }
        return false
    }
}

/** -------------------- Small, readable shape predicates -------------------- */
function isFilePayload(o) {
    return !!o && typeof o === 'object' && 'attributes' in o
}
function isProjectPayload(o) {
    return !!o && typeof o === 'object' && 'swatchColorInfos' in o && !('swatch_color' in o)
}
function isDbColorPayload(o) {
    return !!o && typeof o === 'object' && 'swatch_color' in o
}

export default Color
